import React, { useCallback, useState } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import {
  Grid,
  FormControlLabel,
  Typography,
  IconButton,
  Card,
  CardContent,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from 'recharts';
import { Brush } from 'src/App/Components';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_E,
  COLOR_TOOLTIP_BACKGROUND,
  exchangeRates,
} from 'src/const';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { infoIconStyle, getInfoLink } from '../../Dashboards/Market/styles/style';

import Button from '@mui/material/Button';
import Backdrop from '@material-ui/core/Backdrop';
import { Link } from 'react-router-dom';
import { PacingData } from 'src/interfaces/idashboard';
import { RateAnalysisPropTypes } from 'src/interfaces/iproptypes';
import { InfoTooltip } from '../Tooltips';
import mixpanel from 'mixpanel-browser';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useRateAnalysisChartClasses = makeStyles(() => ({
  checkboxRoot: ({ color }: { color?: string }) => ({
    color: `${color} !important`,
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  }),
  labelRoot: ({ color }: { color?: string }) => ({
    color,
    fontSize: '.9rem',
    fontWeight: 500,
  }),
  rateAnalysisChart: {
    marginBottom: '1vh',
  },
  rateAnalysisChartHeaderText: {
    fontSize: 17,
  },
  iconButton: {
    padding: 5,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 100,
  },
  sampleText: {
    color: COLOR_E,
    fontWeight: 600,
    transform: 'rotate(-30deg)',
    msTransform: 'rotate(-30deg)' /* IE 9 */,
    webkitTransform: 'rotate(-30deg),' /* Opera, Chrome, and Safari */,
  },
  blurGraph: {
    webkitFilter: 'blur(2px)',
  },
}));

const hoverGraph = () => {
  mixpanel.track('checkTooltipOnCompset');
};

const RateAnalysisChart = ({
  cardTitle,
  data,
  colors,
  titles,
  isUserAllowed,
  search,
  totalSupply,
  updatedDate,
  isRange,
  referenceAvailData,
  currency,
}: RateAnalysisPropTypes) => {
  const classes = useRateAnalysisChartClasses({});

  let isDisabled: boolean = !isUserAllowed;

  let dataSet: string[] = [];
  let name: string = '';
  let dataType1: string = '';
  let dataType2: string = '';

  data.forEach((keys: PacingData) => {
    dataSet = Object.keys(keys);
    dataType1 = dataSet[0];
    dataType2 = dataSet[1];
    name = dataSet[2];
  });

  const classesData1 = useRateAnalysisChartClasses({ color: colors.color1 });
  const classesData2 = useRateAnalysisChartClasses({ color: colors.color2 });

  const [visibleDatasets, changeVisibleDatasets] = useState<{
    dataType1: boolean;
    dataType2: boolean;
  }>({
    dataType1: true,
    dataType2: true,
  });

  const handleChange = useCallback(
    ({ target }) => {
      changeVisibleDatasets({ ...visibleDatasets, [target.name]: target.checked });
    },
    [changeVisibleDatasets, visibleDatasets]
  );

  const CustomTooltip = ({ active, payload, label, classes }) => {
    if (active && payload) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {payload[0].payload.date}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {getTooltipText(payload[0].payload[dataType1], dataType1, currency)}
              <br />
              {getTooltipText(payload[0].payload[dataType2], dataType2, currency)}
            </Typography>
          </CardContent>
        </Card>
      );
    }
    return null;
  };

  const formatDate = (dateString: Date): string => {
    const dateStringParsed = dateString.toString();
    let year: number = Number(dateStringParsed.substring(0, 4));
    let month: number = Number(dateStringParsed.substring(5, 7));
    let day: number = Number(dateStringParsed.substring(8));

    let tempMonth: number = month - 1; // For 2021-02-25 we will have to pass 2021-01-25
    const date = new Date(year, tempMonth, day); // 2021-01-25 => Feb
    const monthName: string = date.toLocaleString('default', { month: 'short' });

    const visibleDate: string = `${monthName} ${day}`;
    return visibleDate;
  };

  const N = 5;
  let blurGraph = {};

  if (isDisabled) {
    blurGraph = {
      webkitFilter: 'blur(2px)',
    };
  }

  return (
    <Grid item lg={12} md={12} xs={12} justify="center">
      <Card className={classes.rateAnalysisChart}>
        <CardContent>
          <Grid container direction="row" justify="space-between" alignItems="center">
            <Typography className={classes.rateAnalysisChartHeaderText} gutterBottom>
              {cardTitle}
              <InfoTooltip
                title={
                  <Card>
                    <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                      <Grid container direction="column" alignItems="flex-start">
                        <Typography variant="body1" style={{ fontWeight: 900 }}>
                          Available Daily Rate
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 400 }}>
                          The median rate of all listings that are available on a certain night.
                        </Typography>
                        <br />
                        <Typography variant="body1" style={{ fontWeight: 900 }}>
                          Booked Daily Rate
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 400 }}>
                          The median rate of all listings that are booked on a certain night.
                        </Typography>
                        <br />
                        <Button
                          variant="contained"
                          sx={{
                            background: COLOR_PRIMARY_DARK,
                            '&:hover': {
                              background: COLOR_PRIMARY,
                            },
                          }}
                          onClick={() => window.open(getInfoLink())}
                        >
                          Know More
                        </Button>
                      </Grid>
                    </CardContent>
                  </Card>
                }
              >
                <IconButton className={classes.iconButton}>
                  <InfoOutlinedIcon style={infoIconStyle} />
                </IconButton>
              </InfoTooltip>
            </Typography>

            {updatedDate && (
              <Typography className={classes.rateAnalysisChartHeaderText} gutterBottom>
                Updated on {updatedDate}
              </Typography>
            )}
          </Grid>

          <Grid>
            <Grid>
              {dataType1 && !isRange && (
                <FormControlLabel
                  classes={{ label: classesData1.labelRoot }}
                  control={
                    <Checkbox
                      disableRipple
                      checked={visibleDatasets.dataType1}
                      onChange={handleChange}
                      name="dataType1"
                      classes={{ root: classesData1.checkboxRoot }}
                    />
                  }
                  label={titles.title1}
                />
              )}
              {dataType2 && !isRange && (
                <FormControlLabel
                  classes={{ label: classesData2.labelRoot }}
                  control={
                    <Checkbox
                      disableRipple
                      checked={visibleDatasets.dataType2}
                      onChange={handleChange}
                      name="dataType2"
                      classes={{ root: classesData2.checkboxRoot }}
                    />
                  }
                  label={titles.title2}
                />
              )}
            </Grid>

            <Grid
              container
              item
              lg={12}
              md={12}
              xs={12}
              style={{ position: 'relative', top: '0', left: '0' }}
            >
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  width="100%"
                  height={200}
                  data={data}
                  style={blurGraph}
                  onMouseEnter={hoverGraph}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={name}
                    style={{ fontFamily: 'Roboto' }}
                    interval={15}
                    tickFormatter={formatDate}
                  />
                  <YAxis style={{ fontFamily: 'Roboto' }} />
                  <Tooltip content={<CustomTooltip classes={classes} active payload label />} />
                  {isRange && referenceAvailData && (
                    <ReferenceLine
                      y={referenceAvailData}
                      label={{
                        value: dataType1,
                        fontFamily: 'Roboto',
                        fontSize: 14,
                        fill: COLOR_PRIMARY_DARK,
                      }}
                      stroke={colors.color1}
                    />
                  )}
                  {visibleDatasets.dataType1 && !isRange && (
                    <Line
                      type="monotone"
                      dataKey={dataType1}
                      stroke={colors.color1}
                      fill={colors.color1}
                      activeDot={{ r: 6 }}
                    />
                  )}
                  {visibleDatasets.dataType2 && (
                    <Line
                      type="monotone"
                      dataKey={dataType2}
                      stroke={colors.color2}
                      fill={colors.color2}
                      activeDot={{ r: 6 }}
                    />
                  )}
                  <Brush
                    height={20}
                    stroke="#E0E0E0"
                    travellerStroke="#777777"
                    fill="transparent"
                  />
                </LineChart>
              </ResponsiveContainer>

              {/* {isDisabled && search && totalSupply && (
                <Backdrop className={classes.backdrop} open={true} invisible={true}>
                  <Grid container direction="row" justify="space-around" alignItems="center">
                    {[...Array(N)].map((_, i) => (
                      <Typography variant="h5" className={classes.sampleText}>
                        <Link
                          to={{
                            pathname: getPaymentURL(search.data),
                            state: { totalSupply: totalSupply },
                          }}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          SAMPLE
                        </Link>
                      </Typography>
                    ))}
                  </Grid>
                </Backdrop>
              )} */}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

const getTooltipText = (val: number, title: string, currency: string | undefined) => {
  if (title.includes('Rate')) {
    return `${title}: ${currency ? exchangeRates[currency].symbol : '$'}${val}`;
  }
  if (title.includes('Nights')) {
    return `${title}: ${val}`;
  }
  return '';
};

export default RateAnalysisChart;
